import { useFileUpload } from "@src/context/FileUploadContext";
import { theme } from "@src/theme";
import React, { useState, useEffect, useRef } from "react";
import TimesIcon from "@assets/img/times-icon.svg";

const UploadProgressModal: React.FC = () => {
  const {
    uploading,
    progress,
    files,
    showProgressModal,
    setShowProgressModal,
    completeUploadBool,
    error: uploadError,
    filesCompletedCount,
    validatingFiles,
  } = useFileUpload();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [modalPosition, setModalPosition] = useState(() => ({
    top: window.innerHeight - 165,
    left: window.innerWidth - 260,
  }));
  const modalRef = useRef<HTMLDivElement>(null);

  // Handlers for dragging the modal
  const handleMouseDown = (): void => {
    setIsExpanded(false);
    setIsDragging(true);
  };

  const handleMouseMove = (event: MouseEvent): void => {
    if (isDragging && modalRef.current) {
      const newX = event.clientX - modalRef.current.offsetWidth / 2;
      const newY = event.clientY - modalRef.current.offsetHeight / 2;
      setModalPosition({ top: newY, left: newX });
    }
  };

  const handleMouseUp = (event: MouseEvent): void => {
    if (isDragging && modalRef.current) {
      const mouseX = event.clientX - modalRef.current.offsetWidth / 2;
      const mouseY = event.clientY - modalRef.current.offsetHeight / 2;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // Calculate distances to the four corners of the screen
      const topLeft = Math.sqrt(Math.pow(mouseX, 2) + Math.pow(mouseY, 2));
      const topRight = Math.sqrt(
        Math.pow(screenWidth - mouseX, 2) + Math.pow(mouseY, 2)
      );
      const bottomLeft = Math.sqrt(
        Math.pow(mouseX, 2) + Math.pow(screenHeight - mouseY, 2)
      );
      const bottomRight = Math.sqrt(
        Math.pow(screenWidth - mouseX, 2) + Math.pow(screenHeight - mouseY, 2)
      );

      // Find the minimum distance and move the modal to the closest corner
      const minDistance = Math.min(topLeft, topRight, bottomLeft, bottomRight);

      if (minDistance === topLeft) {
        setModalPosition({ top: 10, left: 10 });
      } else if (minDistance === topRight) {
        setModalPosition({
          top: 10,
          left: screenWidth - modalRef.current.offsetWidth - 10,
        });
      } else if (minDistance === bottomLeft) {
        setModalPosition({
          top: screenHeight - modalRef.current.offsetHeight - 10,
          left: 10,
        });
      } else {
        setModalPosition({
          top: screenHeight - modalRef.current.offsetHeight - 10,
          left: screenWidth - modalRef.current.offsetWidth - 10,
        });
      }
    }
    if (!isDragging) return;
    setIsDragging(false);
  };

  const handleResize = (): void => {
    requestAnimationFrame(() => {
      if (modalRef.current) {
        const { offsetHeight, offsetWidth } = modalRef.current;
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        setModalPosition((prevPosition) => {
          const bottomDiff = screenHeight - (prevPosition.top + offsetHeight);
          const rightDiff = screenWidth - (prevPosition.left + offsetWidth);

          const nearBottom = bottomDiff <= 20;
          const nearRight = rightDiff <= 20;

          const newTop = nearBottom
            ? screenHeight - offsetHeight - 10
            : Math.min(prevPosition.top, screenHeight - offsetHeight - 10);

          const newLeft = nearRight
            ? screenWidth - offsetWidth - 10
            : Math.min(prevPosition.left, screenWidth - offsetWidth - 10);

          return { top: newTop, left: newLeft };
        });
      }
    });
  };

  // Attach mousemove, mouseup, and resize events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    window.addEventListener("resize", handleResize);

    return (): void => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("resize", handleResize);
    };
  }, [isDragging]);

  useEffect(() => {
    if (showProgressModal || validatingFiles) {
      handleResize();
    }
  }, [showProgressModal, validatingFiles]);

  useEffect(() => {
    if (completeUploadBool === true) {
      setTimeout(() => {
        setIsExpanded(false);
        setShowProgressModal(false);
      }, 10000);
    }
  }, [completeUploadBool]);

  const renderTitle = () => {
    if (uploadError === "Upload cancelled") {
      return "UPLOAD CANCELLED";
    }
    if (validatingFiles) {
      return "VALIDATING FILES...";
    }
    if (uploading && !completeUploadBool) {
      return "UPLOADING...";
    }
    if (completeUploadBool && !uploadError && !uploading && files?.length) {
      return "UPLOAD COMPLETE!";
    }
    return "";
  };
  return (
    <div
      ref={modalRef}
      style={{
        display:
          uploading || showProgressModal || validatingFiles
            ? "inline-block"
            : "none",
        position: "fixed",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        color: "white",
        padding: 20,
        borderRadius: "5px",
        zIndex: 1000,
        width: isExpanded && !isDragging ? "200px" : "200px",
        transition: isDragging ? "none" : "all 0.3s ease",
        cursor: isDragging ? "grabbing" : "grab",
        top: modalPosition.top,
        left: modalPosition.left,
        border: `1px solid ${theme.colors.yellow}`,
        height: "120px",
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => !isDragging && setIsExpanded(true)}
      onMouseLeave={() => !isDragging && setIsExpanded(false)}
    >
      {validatingFiles ? (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            color: theme.colors.yellow,
            userSelect: "none",
          }}
        >
          VALIDATING FILES...
        </div>
      ) : completeUploadBool && uploadError ? (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
              setShowProgressModal(false);
            }}
            style={{
              top: 5,
              left: 3,
              position: "absolute",
              background: "none",
              border: "none",
              color: "white",
            }}
          >
            <img
              src={TimesIcon}
              alt="Close"
              style={{ width: "100%", objectFit: "contain" }}
            />
          </button>
          <div
            style={{
              justifyContent: "space-between",
              display: "flex",
              userSelect: "none",
            }}
          >
            <span
              style={{
                paddingTop: theme.margins.medium,
                userSelect: "none",
                fontWeight: "bold",
                display: "block",
                overflow: "hidden",
                minHeight: 20,
                textOverflow: "ellipsis",
                color: theme.colors.red,
                textAlign: "center",
              }}
            >
              {renderTitle()}
            </span>
          </div>
        </>
      ) : (
        <>
          {completeUploadBool && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
                setShowProgressModal(false);
              }}
              style={{
                top: 5,
                left: 3,
                position: "absolute",
                background: "none",
                border: "none",
                color: "white",
              }}
            >
              <img
                src={TimesIcon}
                alt="Close"
                style={{ width: "100%", objectFit: "contain" }}
              />
            </button>
          )}
          <span
            style={{
              paddingTop: theme.margins.medium,
              userSelect: "none",
              fontWeight: "bold",
              display: "block",
              overflow: "hidden",
              minHeight: 20,
              textOverflow: "ellipsis",
              color:
                uploadError === "Upload cancelled"
                  ? theme.colors.red
                  : completeUploadBool
                    ? theme.colors.green
                    : theme.colors.yellow,
              textAlign: "center",
            }}
          >
            {renderTitle()}
          </span>
          {uploading && (
            <progress
              value={progress}
              max={100}
              style={{
                userSelect: "none",
                width: "100%",
                marginTop: theme.margins.medium,
              }}
            />
          )}
          {completeUploadBool && !uploading && !uploadError && (
            <progress
              className="progress-completed"
              value={100}
              max={100}
              style={{
                userSelect: "none",
                width: "100%",
                marginTop: theme.margins.medium,
              }}
            />
          )}
          <div
            style={{
              justifyContent: "flex-end",
              display: "flex",
              alignItems: "center",
              userSelect: "none",
              marginTop: theme.margins.medium,
            }}
          >
            {files?.length && (
              <p style={{ color: theme.colors.yellow, fontWeight: "bold" }}>
                {`${filesCompletedCount}/${files?.length ?? ""}`}
              </p>
            )}
            {/* {(uploading || validatingFiles) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cancelUpload();
                }}
                style={{
                  background: "none",
                  border: `1px solid ${theme.colors.red}`,
                  borderRadius: "4px",
                  padding: "4px 8px",
                  color: theme.colors.red,
                  fontWeight: "bold",
                  cursor: "pointer",
                  marginLeft: theme.margins.medium,
                }}
              >
                Cancel Upload
              </button>
            )} */}
          </div>
        </>
      )}
    </div>
  );
};

export default UploadProgressModal;
